import type { MemeMetaFile, MemeMetaMesh, ViewpointCoordinateConventionMatch, ViewpointVector3 } from '../generate-jig.types'

export interface ConventionPartLike {
  name: string
  objectName: string
  center?: ViewpointVector3
  size?: ViewpointVector3
}

export type CoordinateConventionMode = 'identity' | 'zFlip' | 'y180'
export type CoordinateConventionResolution = 'resolved' | 'ambiguous' | 'degenerate-reference' | 'missing-reference'
export type CoordinateConventionSource =
  | 'meme-geometry'
  | 'memeMeta-correlation'
  | 'degenerate-mememeta'
  | 'missing-mememeta'
  | 'legacy-v1-metadata'

export interface CoordinateConventionDetectionResult {
  detectionSource: 'memeMeta-correlation' | 'degenerate-mememeta' | 'missing-mememeta'
  source: CoordinateConventionSource
  resolution: CoordinateConventionResolution
  mode?: CoordinateConventionMode
  canCanonicalize: boolean
  zFlipDetected: boolean
  matchedPartCount: number
  xCorrelation: number | null
  zCorrelation: number | null
  matchedParts: ViewpointCoordinateConventionMatch[]
}

interface CorrelationPair {
  localX: number
  localZ: number
  memeX: number
  memeZ: number
}

interface CorrelationResult {
  correlation: number | null
  sourceDegenerate: boolean
  targetDegenerate: boolean
}

function normalizeMeshName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, '_').replace(/_+/g, '_')
}

function stripLikelyGuidPrefix(name: string): string {
  const separatorIndex = name.indexOf('_')
  if (separatorIndex <= 0) {
    return name
  }

  const prefix = name.slice(0, separatorIndex)
  return /^[0-9a-f-]{8,}$/i.test(prefix) ? name.slice(separatorIndex + 1) : name
}

function stripTrailingNumericSuffix(name: string): string {
  if (/^(o_)?mesh_\d+$/i.test(name.trim())) {
    return name
  }

  return name.replace(/_\d+$/, '')
}

function stripLegacyObjectPrefix(name: string): string {
  return name.replace(/^o_/, '')
}

export function buildMeshNameVariants(name?: string): string[] {
  if (!name) {
    return []
  }

  const variants: string[] = []
  const candidates = [
    name,
    stripLikelyGuidPrefix(name),
    stripTrailingNumericSuffix(name),
    stripTrailingNumericSuffix(stripLikelyGuidPrefix(name)),
    stripLegacyObjectPrefix(name),
    stripLegacyObjectPrefix(stripLikelyGuidPrefix(name)),
    stripTrailingNumericSuffix(stripLegacyObjectPrefix(name)),
    stripTrailingNumericSuffix(stripLegacyObjectPrefix(stripLikelyGuidPrefix(name))),
  ]

  candidates.forEach(function (candidate) {
    const normalized = normalizeMeshName(candidate)
    if (normalized && variants.indexOf(normalized) < 0) {
      variants.push(normalized)
    }
  })

  return variants
}

function isMeshLike(mesh: Partial<Pick<MemeMetaMesh, 'Type'>>): boolean {
  return !mesh || !mesh.Type || String(mesh.Type).toUpperCase() === 'MESH'
}

function computeCorrelation(pairs: Array<{ source: number; target: number }>): CorrelationResult {
  if (pairs.length < 2) {
    return {
      correlation: null,
      sourceDegenerate: false,
      targetDegenerate: false,
    }
  }

  const meanSource = pairs.reduce(function (sum, pair) { return sum + pair.source }, 0) / pairs.length
  const meanTarget = pairs.reduce(function (sum, pair) { return sum + pair.target }, 0) / pairs.length

  let numerator = 0
  let sourceVariance = 0
  let targetVariance = 0

  pairs.forEach(function (pair) {
    const sourceDelta = pair.source - meanSource
    const targetDelta = pair.target - meanTarget
    numerator += sourceDelta * targetDelta
    sourceVariance += sourceDelta * sourceDelta
    targetVariance += targetDelta * targetDelta
  })

  const sourceDegenerate = sourceVariance < 1e-10
  const targetDegenerate = targetVariance < 1e-10

  if (sourceDegenerate || targetDegenerate) {
    return {
      correlation: null,
      sourceDegenerate: sourceDegenerate,
      targetDegenerate: targetDegenerate,
    }
  }

  return {
    correlation: numerator / Math.sqrt(sourceVariance * targetVariance),
    sourceDegenerate: false,
    targetDegenerate: false,
  }
}

function blenderPositionToThreePosition(position: ViewpointVector3): ViewpointVector3 {
  return {
    x: position.x,
    y: position.z,
    z: -position.y,
  }
}

export function applyConventionToCenter(
  center: ViewpointVector3 | undefined,
  mode: CoordinateConventionMode
): ViewpointVector3 | undefined {
  if (!center) {
    return undefined
  }

  if (mode === 'identity') {
    return { x: center.x, y: center.y, z: center.z }
  }

  if (mode === 'zFlip') {
    return {
      x: center.x,
      y: center.y,
      z: -center.z,
    }
  }

  return {
    x: -center.x,
    y: center.y,
    z: -center.z,
  }
}

function buildResultFromMatches(
  matchedParts: ViewpointCoordinateConventionMatch[],
  correlationPairs: CorrelationPair[]
): CoordinateConventionDetectionResult {
  const xResult = computeCorrelation(correlationPairs.map(function (pair) {
    return { source: pair.localX, target: pair.memeX }
  }))
  const zResult = computeCorrelation(correlationPairs.map(function (pair) {
    return { source: pair.localZ, target: pair.memeZ }
  }))

  const xCorrelation = xResult.correlation
  const zCorrelation = zResult.correlation

  const xStronglyNegative = xCorrelation !== null && xCorrelation < -0.5
  const zStronglyNegative = zCorrelation !== null && zCorrelation < -0.5
  const zStronglyPositive = zCorrelation !== null && zCorrelation > 0.5

  let mode: CoordinateConventionMode | undefined
  if (xStronglyNegative && zStronglyNegative) {
    mode = 'y180'
  } else if (zStronglyNegative && !xStronglyNegative) {
    mode = 'zFlip'
  } else if (zStronglyPositive && !xStronglyNegative) {
    mode = 'identity'
  }

  let resolution: CoordinateConventionResolution = 'ambiguous'
  let detectionSource: CoordinateConventionDetectionResult['detectionSource'] = 'memeMeta-correlation'
  if (mode) {
    resolution = 'resolved'
  } else if (xResult.targetDegenerate || zResult.targetDegenerate) {
    resolution = 'degenerate-reference'
    detectionSource = 'degenerate-mememeta'
  }

  return {
    detectionSource: detectionSource,
    source: detectionSource,
    resolution: resolution,
    mode: mode,
    canCanonicalize: resolution === 'resolved' && !!mode,
    zFlipDetected: mode === 'zFlip',
    matchedPartCount: matchedParts.length,
    xCorrelation: xCorrelation,
    zCorrelation: zCorrelation,
    matchedParts: matchedParts,
  }
}

function buildNameMatchedAlignment(
  parts: ConventionPartLike[],
  meshLookup: Map<string, MemeMetaMesh[]>
): { matchedParts: ViewpointCoordinateConventionMatch[]; correlationPairs: CorrelationPair[] } {
  const matchedMeshIds = new Set<string>()
  const matchedParts: ViewpointCoordinateConventionMatch[] = []
  const correlationPairs: CorrelationPair[] = []

  parts.forEach(function (part, partIndex) {
    if (!part.center) {
      return
    }

    const partNameVariants = buildMeshNameVariants(part.name).concat(buildMeshNameVariants(part.objectName))
    const dedupedVariants = Array.from(new Set(partNameVariants))

    let memeMesh: MemeMetaMesh | null = null
    for (let i = 0; i < dedupedVariants.length; i++) {
      const candidates = meshLookup.get(dedupedVariants[i])
      if (!candidates || !candidates.length) {
        continue
      }

      const unmatchedCandidate = candidates.find(function (candidate) {
        return !matchedMeshIds.has(String(candidate.MeshID))
      })
      if (!unmatchedCandidate) {
        continue
      }

      memeMesh = unmatchedCandidate
      break
    }

    if (!memeMesh || !memeMesh.AbsoluteWorldTransform || !memeMesh.AbsoluteWorldTransform.Position) {
      return
    }

    matchedMeshIds.add(String(memeMesh.MeshID))
    const memeThreePosition = blenderPositionToThreePosition(memeMesh.AbsoluteWorldTransform.Position)

    matchedParts.push({
      partIndex: partIndex,
      referenceMeshId: String(memeMesh.MeshID),
      referenceMeshName: String(memeMesh.Name || ''),
      memeThreePosition: memeThreePosition,
    })
    correlationPairs.push({
      localX: part.center.x,
      localZ: part.center.z,
      memeX: memeThreePosition.x,
      memeZ: memeThreePosition.z,
    })
  })

  return {
    matchedParts: matchedParts,
    correlationPairs: correlationPairs,
  }
}

export function detectCoordinateConvention(
  parts: ConventionPartLike[],
  memeMetaData?: MemeMetaFile | null
): CoordinateConventionDetectionResult {
  const meshes = memeMetaData && memeMetaData.Asset3DData && Array.isArray(memeMetaData.Asset3DData.Meshes)
    ? memeMetaData.Asset3DData.Meshes
    : null

  if (!meshes || !meshes.length) {
    return {
      detectionSource: 'missing-mememeta',
      source: 'missing-mememeta',
      resolution: 'missing-reference',
      canCanonicalize: false,
      zFlipDetected: false,
      mode: undefined,
      matchedPartCount: 0,
      xCorrelation: null,
      zCorrelation: null,
      matchedParts: [],
    }
  }

  const meshLookup = new Map<string, MemeMetaMesh[]>()
  meshes.forEach(function (mesh) {
    if (!isMeshLike(mesh) || !mesh || !mesh.Name) {
      return
    }

    buildMeshNameVariants(mesh.Name).forEach(function (variant) {
      const matches = meshLookup.get(variant) || []
      matches.push(mesh)
      meshLookup.set(variant, matches)
    })
  })

  const nameMatchedAlignment = buildNameMatchedAlignment(parts, meshLookup)
  return buildResultFromMatches(nameMatchedAlignment.matchedParts, nameMatchedAlignment.correlationPairs)
}
